/**
 * De las medidas al veredicto: ¿puede una persona leer esta zona?
 *
 * Separado de `quality.ts` a propósito. Ahí están las medidas, que son hechos y
 * no cambian. Acá están los cortes, que son decisiones y se van a mover a medida
 * que nos encontremos con fotos que hoy no tenemos. Los cortes viven en
 * `tuning.ts`; este archivo solo los aplica.
 */
import {
  LegibilidadDeZona,
  MedidasDeZona,
  MotivoIlegible,
  VeredictoLegibilidad,
} from "./types";
import { LEGIBILIDAD } from "./tuning";

export function evaluarZona(zona: string, m: MedidasDeZona): LegibilidadDeZona {
  const motivos: MotivoIlegible[] = [];
  let veredicto: VeredictoLegibilidad = "legible";

  const peor = (v: VeredictoLegibilidad) => {
    const orden: VeredictoLegibilidad[] = ["legible", "dudosa", "ilegible"];
    if (orden.indexOf(v) > orden.indexOf(veredicto)) veredicto = v;
  };

  if (m.desenfoque > LEGIBILIDAD.desenfoqueIlegible) {
    motivos.push("fuera_de_foco");
    peor("ilegible");
  } else if (m.desenfoque > LEGIBILIDAD.desenfoqueDudoso) {
    motivos.push("fuera_de_foco");
    peor("dudosa");
  }

  if (m.reflejo > LEGIBILIDAD.reflejoIlegible) {
    motivos.push("reflejo");
    peor("ilegible");
  } else if (m.reflejo > LEGIBILIDAD.reflejoDudoso) {
    motivos.push("reflejo");
    peor("dudosa");
  }

  if (m.oscuridad > LEGIBILIDAD.oscuridadMaxima) {
    motivos.push("muy_oscura");
    peor("dudosa");
  }

  // El alto de renglón solo se puede afirmar si se ubicó la tarjeta. Cuando no
  // se midió, no se inventa: simplemente no aporta motivo.
  if (m.altoRenglonPx != null && m.altoRenglonPx < LEGIBILIDAD.altoRenglonMinimoPx) {
    motivos.push("muy_chica");
    peor(m.altoRenglonPx < LEGIBILIDAD.altoRenglonMinimoPx * 0.6 ? "ilegible" : "dudosa");
  }

  return { zona, veredicto, motivos, medidas: m };
}

/**
 * El peor veredicto entre varias zonas, que es el que manda: alcanza con que el
 * domicilio esté tapado por un reflejo para que la foto no sirva, por más que el
 * resto se lea perfecto.
 */
export function veredictoGeneral(zonas: LegibilidadDeZona[]): VeredictoLegibilidad {
  if (zonas.length === 0) return "sin_medir";
  if (zonas.some((z) => z.veredicto === "ilegible")) return "ilegible";
  if (zonas.some((z) => z.veredicto === "dudosa")) return "dudosa";
  return "legible";
}

/** Motivos únicos, en orden de aparición, para armar un mensaje sin repetir. */
export function motivosDe(zonas: LegibilidadDeZona[]): MotivoIlegible[] {
  const vistos: MotivoIlegible[] = [];
  for (const z of zonas) {
    for (const m of z.motivos) if (!vistos.includes(m)) vistos.push(m);
  }
  return vistos;
}
